import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from '@mc/i18n'
import { debounce } from 'lodash'

import Logo from '../../assets/oncall-logo.svg'

import styles from './OrientationLock.module.scss'

export const OrientationLock = () => {
  const [isLandscape, setIsLandscape] = useState(false)
  const lastWidth = useRef<number>(0)
  const initialCheckDone = useRef<boolean>(false)

  const { t } = useTranslation('@mc/persona')

  const checkOrientation = useCallback(() => {
    if (typeof window !== 'undefined') {
      const { innerWidth, innerHeight } = window
      const aspectRatio = innerWidth / innerHeight

      // Only show orientation lock on mobile devices (width <= 1024px)
      const isMobileDevice = innerWidth <= 1024

      // Consider a device in landscape if:
      // 1. It's a mobile device
      // 2. The aspect ratio is significantly greater than 1 (1.2 to account for folding phones)
      // 3. The width is at least 600px (to avoid triggering on narrow portrait views)
      const isLandscapeMode =
        isMobileDevice && aspectRatio > 1.2 && innerWidth >= 600

      // Only update the landscape state if:
      // 1. This is the first check, or
      // 2. The width has changed significantly (more than 10%) to account for keyboard appearance in folding phones.
      // Where viewport is resized and aspect ratio can go from portrait to landscape
      const widthChangedSignificantly =
        !initialCheckDone.current ||
        Math.abs(innerWidth - lastWidth.current) / lastWidth.current > 0.1

      if (widthChangedSignificantly) {
        setIsLandscape(isLandscapeMode)
        lastWidth.current = innerWidth
        initialCheckDone.current = true
      }
    }
  }, [])

  useEffect(() => {
    // Check initial orientation
    checkOrientation()

    // Debounce the orientation check to prevent rapid toggling
    const debouncedCheck = debounce(checkOrientation, 250)

    // Add listeners for orientation and resize changes
    window.addEventListener('resize', debouncedCheck)
    window.addEventListener('orientationchange', debouncedCheck)

    return () => {
      window.removeEventListener('resize', debouncedCheck)
      window.removeEventListener('orientationchange', debouncedCheck)
    }
  }, [checkOrientation])

  if (!isLandscape) return null

  return (
    <div
      className={`container d-flex justify-content-center align-items-center flex-column ${styles.container}`}
    >
      <div className={styles.background} />
      <Logo className={styles.logo} />
      <h1 className='mc-text-h2 mc-my-4'>
        {t('common.orientationLock.heading')}
      </h1>
      <div className={`row justify-content-center ${styles.textContainer}`}>
        <p className='mc-text-medium col-8 mc-opacity--muted'>
          {t('common.orientationLock.text')}
        </p>
      </div>
    </div>
  )
}
